import {
  TemplateHeader,
  PatientDetailsSection,
  TemplateFooter,
} from './TemplateShared';
import { DASH } from '../utils/displayValues';
import { SignedUrlPreview } from './SignedUrlPreview';
import {
  NOT_AVAILABLE,
  formatLongDate,
  formatPractitionerName,
} from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';

export type PathologyTemplatePractitioner = TemplatePractitioner;

export interface PathologyTemplateProcedure {
  id: string;
  name?: string | null;
  dateOfStudy?: string | null;
  reason?: string;
  asserter?: PathologyTemplatePractitioner;
  performer?: PathologyTemplatePractitioner;
}

export interface PathologyTemplateProblem {
  id: string;
  name?: string | null;
  onsetDate?: string;
  recordedDate?: string;
  clinicalStatus?: string;
  conditionNote?: string;
}

export interface PathologyTemplateSource {
  id?: string;
  contentType?: string;
  signedUrl?: string;
  procedure?: PathologyTemplateProcedure;
  problem?: PathologyTemplateProblem;
}

export interface PathologyTemplateDetail {
  id: string;
  sourceType?: string;
  sources: PathologyTemplateSource[];
}

export interface PathologyTemplateProps {
  details: PathologyTemplateDetail[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightDetailId?: string;
}

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className="flex items-start gap-3 border-b border-zinc-200 px-4 py-2.5 last:border-b-0">
    <p className="w-32 shrink-0 text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {label}
    </p>
    <p className="flex-1 text-[11px] leading-tight whitespace-pre-line text-zinc-950">
      {value?.trim() || DASH}
    </p>
  </div>
);

const PractitionerCard = ({
  label,
  practitioner,
}: {
  label: string;
  practitioner?: PathologyTemplatePractitioner;
}) => (
  <div className="flex flex-col gap-1 rounded-sm border border-zinc-200 bg-slate-50 px-3 py-2">
    <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
      {label}
    </p>
    <p className="text-[11px] leading-tight font-semibold text-zinc-950">
      {formatPractitionerName(practitioner)}
    </p>
    <p className="text-[10px] leading-tight text-zinc-700">
      {practitioner?.roleSpecialty || practitioner?.role || NOT_AVAILABLE}
    </p>
  </div>
);

const SourceSection = ({
  source,
  index,
}: {
  source: PathologyTemplateSource;
  index: number;
}) => (
  <div className="flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white px-4 py-3">
    <p className="text-[11px] leading-tight font-bold text-zinc-700">
      Source {index + 1}
    </p>

    {source.signedUrl ? (
      <div className="flex h-48 w-full items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-100">
        <SignedUrlPreview
          signedUrl={source.signedUrl}
          contentType={source.contentType}
          title={`Pathology source ${index + 1} preview`}
        />
      </div>
    ) : null}

    {source.procedure ? (
      <div className="flex flex-col gap-2">
        <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
          Procedure
        </p>
        <div className="overflow-hidden rounded-sm border border-zinc-200">
          <InfoRow label="Name" value={source.procedure.name} />
          <InfoRow
            label="Date of Study"
            value={formatLongDate(source.procedure.dateOfStudy)}
          />
          <InfoRow label="Reason" value={source.procedure.reason} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <PractitionerCard
            label="Asserter"
            practitioner={source.procedure.asserter}
          />
          <PractitionerCard
            label="Performer"
            practitioner={source.procedure.performer}
          />
        </div>
      </div>
    ) : null}

    {source.problem ? (
      <div className="flex flex-col gap-2">
        <p className="text-[10px] leading-tight font-bold tracking-[0.35px] text-zinc-500 uppercase">
          Problem
        </p>
        <div className="overflow-hidden rounded-sm border border-zinc-200">
          <InfoRow label="Name" value={source.problem.name} />
          <InfoRow
            label="Onset Date"
            value={formatLongDate(source.problem.onsetDate)}
          />
          <InfoRow
            label="Recorded Date"
            value={formatLongDate(source.problem.recordedDate)}
          />
          <InfoRow
            label="Clinical Status"
            value={source.problem.clinicalStatus}
          />
          <InfoRow label="Note" value={source.problem.conditionNote} />
        </div>
      </div>
    ) : null}

    {!source.procedure && !source.problem && !source.signedUrl ? (
      <p className="text-[10px] leading-tight text-zinc-500 italic">
        {NOT_AVAILABLE}
      </p>
    ) : null}
  </div>
);

const PathologyCard = ({
  detail,
  index,
  highlightDetailId,
}: {
  detail: PathologyTemplateDetail;
  index: number;
  highlightDetailId?: string;
}) => {
  const isHighlighted = !!highlightDetailId && detail.id === highlightDetailId;
  return (
    <article
      id={`pathology-${detail.id}`}
      className={`flex flex-col overflow-hidden rounded-sm border-2 ${
        isHighlighted ? 'border-amber-400' : 'border-gray-200'
      } bg-white pb-4`}
    >
      <header className="flex flex-col gap-1.5 bg-slate-700 px-5 pt-5 pb-3 text-white">
        <p className="text-[10px] leading-tight font-bold tracking-[0.35px] uppercase opacity-80">
          Pathology Result {index + 1}
        </p>
        <p className="text-[14px] leading-tight font-semibold">
          {detail.sourceType || 'Pathology Result'}
        </p>
      </header>

      <div className="mx-5 mt-3 flex flex-col gap-3">
        {detail.sources.length === 0 ? (
          <p className="rounded-sm border border-zinc-200 bg-slate-50 px-3 py-2 text-[10px] leading-tight text-zinc-500 italic">
            No source data available
          </p>
        ) : (
          detail.sources.map((source, sIdx) => (
            <SourceSection
              key={source.id ?? `src-${sIdx}`}
              source={source}
              index={sIdx}
            />
          ))
        )}
      </div>
    </article>
  );
};

export const PathologyTemplate = ({
  details,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightDetailId,
}: PathologyTemplateProps) => {
  const items = details ?? [];
  const generated = generatedAt ?? new Date();

  return (
    <div className="mx-auto flex min-h-[1056px] w-[816px] flex-col bg-white font-sans text-black">
      <TemplateHeader />

      <section className="flex flex-col gap-4 bg-gray-100 px-8 pt-8 pb-5">
        <h1 className="text-[28px] leading-tight font-bold text-black">
          Pathology Results Report
        </h1>
        <PatientDetailsSection patient={patient} />
      </section>

      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {items.length === 0 ? (
          <div className="rounded-sm border-2 border-gray-200 bg-white px-5 py-12 text-center text-[12px] text-zinc-500">
            No pathology results available
          </div>
        ) : (
          items.map((detail, index) => (
            <PathologyCard
              key={detail.id}
              detail={detail}
              index={index}
              highlightDetailId={highlightDetailId}
            />
          ))
        )}
      </section>

      <TemplateFooter
        filename={filename}
        generatedAt={generated}
        pageInfo={pageInfo}
      />
    </div>
  );
};
